import { forwardRef, useEffect, useState } from "react";

import { AccordionHeaderText, AccordionHeader } from "../../../../components";
import { PAGE_IDS } from "../../../../helpers/constants";
import { useSiteCore } from "../../../../hooks/useSiteCore";

import { BillingDetailsAndConfigurationResponse } from "../../../../api/services/client/models";
import { BillingDetailsCheckboxState } from "../../modelsAndSchemas/Models";
import { billingAndPurchaseOrderKeys } from "../../siteCore/SiteCoreKeys";
import { BillingPurchaseOrder } from "../billingPurchaseOrder/BillingPurchaseOrder";
import { ClientBillingPurchaseOrderMainWrapper } from "./ClientBillingPurchaseOrderMain.style";
import { useSelector } from "react-redux";
import { editClientSelector } from "../../../../redux/slices/client/createClientSelector";
import { useFormContext } from "react-hook-form";

export const pageId = PAGE_IDS.BILLING_AND_PURCHASE_ORDER;
export interface ClientBillingPurchaseOrderMainProps {
  parentAccordionStatus: boolean;
  onAccordionChange: (expanded: boolean) => void;
  isNewContactAdded?: boolean;
  setIsNewContactAdded?: (isNewContactAdded: boolean) => void;
  onConfigOrProgramTemplateDeleted?: (isDeleted: boolean) => void;
  triggerAddContactModal?: () => void;
}

export const ClientBillingPurchaseOrderMain = forwardRef(
  (props: ClientBillingPurchaseOrderMainProps, _ref) => {
    const {
      parentAccordionStatus,
      onAccordionChange,
      isNewContactAdded,
      setIsNewContactAdded,
      onConfigOrProgramTemplateDeleted,
      triggerAddContactModal,
    } = props;
    const { fetchComponentTitle } = useSiteCore();

    const { getValues } = useFormContext();
    const { isEdit: isEditClient } = useSelector(editClientSelector);

    const [billingAndPOAccordionStatus, setBillingAndPOAccordionStatus] =
      useState(parentAccordionStatus);

    useEffect(() => {
      setBillingAndPOAccordionStatus(parentAccordionStatus);
    }, [parentAccordionStatus]);

    const [checkboxState, setCheckboxState] =
      useState<BillingDetailsCheckboxState>({
        consolidatedInvoicing: false,
        activeCompany: true,
        rebateClient: false,
        earlyPayDiscount: false,
      });

    useEffect(() => {
      const {
        activeCompany: activeCompanyValue,
        rebateClient: rebateClientValue,
        consolidatedInvoicing: consolidatedInvoicingValue,
        earlyPayDiscount: earlyPayDiscountValue,
      } = getValues();

      consolidatedInvoicingValue &&
        updateCheckboxState("consolidatedInvoicing");
      activeCompanyValue &&
        activeCompanyValue !== checkboxState.activeCompany &&
        updateCheckboxState("activeCompany");
      rebateClientValue && updateCheckboxState("rebateClient");
      earlyPayDiscountValue && updateCheckboxState("earlyPayDiscount");
    }, [isEditClient]);

    const updateCheckboxState = (key: string) => {
      setCheckboxState((prevState) => ({
        ...prevState,
        [key]: !prevState[key as keyof typeof prevState],
      }));
    };

    const handleCreateClientBillingAndPOChange = (
      _event: React.SyntheticEvent,
      expanded: boolean,
    ) => {
      setBillingAndPOAccordionStatus(expanded);
      onAccordionChange(expanded);
    };

    return (
      <ClientBillingPurchaseOrderMainWrapper>
        <AccordionHeader
          title={
            <AccordionHeaderText
              label={fetchComponentTitle(
                billingAndPurchaseOrderKeys.billingDetailsAndPOTitle,
              )}
            />
          }
          onChange={handleCreateClientBillingAndPOChange}
          isExpanded={billingAndPOAccordionStatus}
        >
          <BillingPurchaseOrder
            updateCheckboxState={updateCheckboxState}
            checkboxState={checkboxState}
            isNewContactAdded={isNewContactAdded}
            setIsNewContactAdded={setIsNewContactAdded}
            onConfigOrProgramTemplateDeleted={onConfigOrProgramTemplateDeleted}
            triggerAddContactModal={triggerAddContactModal}
            isEditClient={isEditClient}
          />
        </AccordionHeader>
      </ClientBillingPurchaseOrderMainWrapper>
    );
  },
);
